import React, { useEffect, useRef, useState } from 'react';
import { Card, Col, Row, Spinner } from 'reactstrap';
import { ChangeDateFormat } from '../../Common/Helpers/ChangeDateFormat';
import { ToCustomDecimal } from '../../AppSystem/Helpers/ToCustomDecimal';
import { useTranslation } from 'react-i18next';
import { ReimbursementTypeBadges, getReimbursementTypesFromReimbursement } from '../../Common/Components/Badges/ReimbursementTypeBadges';
import { CheckMobile } from '../../AppSystem/Helpers/CheckMobile';

const RelatedActionMenu = ({ label, onSelect }) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div ref={menuRef} className="reimbursement-action-menu-wrap" onClick={(e) => e.stopPropagation()}>
            <div
                onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
                className="reimbursement-action-menu-trigger"
            >
                <i className={`bx ${isOpen ? 'bx-x' : 'bx-dots-vertical-rounded'} reimbursement-action-menu-icon`} />
            </div>
            {isOpen && (
                <div className="reimbursement-action-menu-dropdown">
                    <div className="action-menu-item">
                        <button
                            type="button"
                            className="reimbursement-action-menu-entry"
                            onClick={() => { setIsOpen(false); onSelect(); }}
                        >
                            <span className="reimbursement-action-menu-entry-icon"><i className="bx bx-show" /></span>
                            <span className="reimbursement-action-menu-entry-label">{label}</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export function RelatedProceedings({ relatedData, data, onPageChange, page, setPage, handleClick, visibleCount, onSort, sortConfig, isLoading, embedded = false }) {
    const { t } = useTranslation();
    const isMobile = CheckMobile(998);

    if (!(relatedData?.items?.length >= 1)) {
        return null;
    }

    const statusPill = (status) => {
        switch (status) {
            case 'Nekad':
                return <div className="box-red reimbursement-status-pill-auto">{t('attest.denied')}</div>;
            case 'Skickat':
                return <div className="box-custom-grey reimbursement-status-pill-auto">{t('reimbursement.sent')}</div>;
            case 'Godkänd':
                return <div className="box-green reimbursement-status-pill-auto">{t('attest.approved')}</div>;
            case 'Utbetald':
                return <div className="box-green reimbursement-status-pill-auto">{t('modelLabel.paidOut')}</div>;
            case 'Avbruten':
                return <div className="box-red reimbursement-status-pill-auto">{t('honorarium.terminated')}</div>;
            default:
                return '';
        }
    };

    const SortHeader = ({ field, label, className = '' }) => (
        <th onClick={() => onSort(field)} className={`sortable-header ${className}`} style={{ textAlign: "left" }}>
            {label}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginLeft: '5px' }}>
                {sortConfig.key === field && (
                    sortConfig.direction === 'asc'
                        ? <>↑ {isLoading && <Spinner animation="border" variant="success" size="sm" />}</>
                        : <>↓ {isLoading && <Spinner animation="border" variant="success" size="sm" />}</>
                )}
            </span>
        </th>
    );

    const table = (
        <div className="table-responsive">
            <table className="table">
                <thead className="text-left">
                    <tr>
                        <SortHeader field="Model" label={t('modelLabel.type')} />
                        <SortHeader field="ActivityDate" label={t('attest.activityDate')} />
                        <SortHeader field="Recipient" label={t('modelLabel.recipient')} />
                        <SortHeader field="OrgName" label={t('attest.organisation')} />
                        <SortHeader field="Purpose" label={t('attest.purpose')} />
                        <SortHeader field="CreatedAt" label={t('attest.createdAt')} />
                        <SortHeader field="Amount" label={t('attest.amount')} />
                        <SortHeader field="Status" label={t('modelLabel.status')} />
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {relatedData.items.map((item, index) => {
                        const relatedModel = item.relatedModel || {};
                        return (
                            <tr key={index}>
                                <td className="text-left align-middle">
                                    {item.attest?.model === "Arvode" ? t('honorarium.honorarium') : t('reimbursement.reportProcessing')}
                                </td>
                                <td className="text-left align-middle">
                                    {relatedModel.date ? ChangeDateFormat('date', relatedModel.date) : ''}
                                </td>
                                <td className="text-left align-middle">
                                    {item.attest?.recipient || ''}
                                </td>
                                <td className="text-left align-middle">
                                    {item.attest?.orgName || ''}
                                </td>
                                <td className="text-left align-middle">
                                    {relatedModel.purpose || ''}
                                </td>
                                <td className="text-left align-middle">
                                    {item.attest?.createdAt ? ChangeDateFormat('dateTime', item.attest.createdAt) : ''}
                                </td>
                                <td className={`text-end align-middle ${item.attest?.amount >= 0 ? 'text-green' : 'text-red'}`}
                                    style={{ textAlign: "right", paddingRight: "3rem" }}>
                                    {item.attest?.amount !== undefined ? `${ToCustomDecimal(item.attest.amount)} kr` : ''}
                                </td>
                                <td className="text-left align-middle">
                                    {relatedModel.status ? statusPill(relatedModel.status) : ''}
                                </td>
                                <td className="text-left align-middle">
                                    <button
                                        className="rounded me-2 py-2 border-0 text-green bg-green-light p-custom-button position-relative class-approve"
                                        onClick={() => handleClick(item.attest?.id)}
                                    >
                                        <i className="bx bx-link-external pb-custom align-middle size-icon-custom-button"></i>
                                        <div className="tooltip-custom-button">
                                            {t('button.info')}
                                        </div>
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    const paginationControls = relatedData.pagination?.totalPages > 1 ? (
        <div className="att-related-pagination">
            <button
                type="button"
                aria-label={t('accessibility.prevPage')}
                onClick={() => {
                    onPageChange(page - 1);
                    setPage(page - 1);
                }}
                disabled={page <= 1}
            >
                <i className="bx bx-left-arrow-alt" />
            </button>
            <button
                type="button"
                aria-label={t('accessibility.nextPage')}
                onClick={() => {
                    onPageChange(page + 1);
                    setPage(page + 1);
                }}
                disabled={page === relatedData.pagination.totalPages}
            >
                <i className="bx bx-right-arrow-alt" />
            </button>
        </div>
    ) : null;

    /*
    Embedded: rendered inside the attest detail. Uses the same table design as the
    report-proceeding list (sl-* classes) instead of the plain bootstrap table.
    */
    /*
    Mobile: the same cards the report-proceeding list renders, so the section reads
    like the rest of the app instead of a table that scrolls sideways.
    */
    if (embedded && isMobile) {
        const statusMeta = (status) => {
            switch (status) {
                case "Godkänd": return { label: "Godkänd för utbetalning", bg: "#D9F1E6", color: "#157553" };
                case "Skickat": return { label: "Inväntar godkännande", bg: "#DDE7FA", color: "#2B5BB8" };
                case "Nekad": return { label: "Nekade ärenden", bg: "#F8DCDC", color: "#C0392B" };
                case "Förslag": return { label: "Utkast", bg: "#FFF4CC", color: "#8A6500" };
                default: return { label: status || "", bg: "#E5E7EB", color: "#374151" };
            }
        };

        return (
            <>
                <div className="reimbursement-cards-list">
                    {relatedData.items.map((item, index) => {
                        const relatedModel = item.relatedModel || {};
                        const status = relatedModel.status;
                        const meta = statusMeta(status);
                        const isPaidStatus = status === "Utbetald";
                        const paidDateText = relatedModel.paidDateTime ? ChangeDateFormat('date', relatedModel.paidDateTime) : '';
                        const deniedReason = status === "Nekad" ? (relatedModel.reason ?? '').trim() : '';
                        const amount = item.attest?.amount ?? 0;
                        const dateText = item.attest?.createdAt ? ChangeDateFormat('date', item.attest.createdAt) : '';

                        return (
                            <div
                                key={`relatedCard${index}`}
                                className="reimbursement-card-item"
                                onClick={() => handleClick(item.attest?.id)}
                            >
                                <div className="reimbursement-card-content-row">
                                    <div className="reimbursement-card-left">
                                        <div className="reimbursement-card-purpose">
                                            {relatedModel.purpose || "-"}
                                        </div>
                                        {item.attest?.orgName ?
                                            <div className="reimbursement-card-org">{item.attest.orgName}</div>
                                        : ''}
                                        {isPaidStatus && paidDateText ?
                                            <div className="sl-status-paid">{`Utbetald: ${paidDateText}`}</div>
                                        : ''}
                                        {deniedReason ?
                                            <div className="reimbursement-card-denied-reason">
                                                Anledning: "{deniedReason}"
                                            </div>
                                        : ''}
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <div style={{
                                                display: "inline-flex",
                                                padding: "3px 8px",
                                                borderRadius: 999,
                                                fontSize: 11.5,
                                                fontWeight: 800,
                                                background: meta.bg,
                                                color: meta.color
                                            }}>
                                                {meta.label}
                                            </div>
                                            {item.attest?.model === "Arvode"
                                                ? <span className="reimbursement-card-org">{t('honorarium.honorarium')}</span>
                                                : <ReimbursementTypeBadges types={getReimbursementTypesFromReimbursement(relatedModel)} />}
                                        </div>
                                    </div>

                                    <div className="reimbursement-card-right">
                                        <div className="reimbursement-card-right-top">
                                            <div className={`reimbursement-card-amount ${amount >= 0 ? 'reimbursement-card-amount-positive' : 'reimbursement-card-amount-negative'}`}>
                                                {ToCustomDecimal(amount)} kr
                                            </div>
                                            <div className="reimbursement-card-actions-slot">
                                                <RelatedActionMenu
                                                    label={t('button.info')}
                                                    onSelect={() => handleClick(item.attest?.id)}
                                                />
                                            </div>
                                        </div>
                                        {dateText ?
                                            <div className="reimbursement-card-date">{dateText}</div>
                                        : ''}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {paginationControls}
            </>
        );
    }

    if (embedded) {
        const SlSortHeader = ({ field, label, alignRight = false, nowrap = false }) => (
            <th
                className={`sl-th-base ${alignRight ? 'att-related-amount' : ''} ${nowrap ? 'att-related-nowrap' : ''}`}
                onClick={() => onSort(field)}
            >
                <button type="button" className="sl-th-btn">
                    {label}
                    {sortConfig.key === field
                        ? <>
                            <span className="sl-sort-icon-active">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                            {isLoading && <Spinner animation="border" variant="success" size="sm" className="sl-sort-spinner" />}
                        </>
                        : <span className="sl-sort-icon-inactive">↕</span>}
                </button>
            </th>
        );

        return (
            <div className="sl-wrap">
                <div className="sl-card-shell">
                    <div className="att-related-scroll">
                        <table className="sl-table sl-table-layout">
                            <thead>
                                <tr className="sl-table-head-row">
                                    <th className="sl-th-base sl-th-no-sort att-related-nowrap">
                                        <span className="sl-th-label-static">{t('modelLabel.id')}</span>
                                    </th>
                                    <SlSortHeader field="ActivityDate" label={t('modelLabel.date')} nowrap={true} />
                                    <SlSortHeader field="Purpose" label={t('reimbursement.purpose')} />
                                    <th className="sl-th-base sl-th-no-sort">
                                        <span className="sl-th-label-static">{t('modelLabel.type')}</span>
                                    </th>
                                    <SlSortHeader field="OrgName" label={t('modelLabel.org')} />
                                    <SlSortHeader field="Amount" label={t('modelLabel.grossAmount')} alignRight={true} nowrap={true} />
                                    <SlSortHeader field="CreatedAt" label={t('modelLabel.createdAt')} nowrap={true} />
                                    <SlSortHeader field="Status" label={t('modelLabel.status')} nowrap={true} />
                                    <th className="sl-th-base sl-th-action"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {relatedData.items.map((item, index) => {
                                    const relatedModel = item.relatedModel || {};
                                    const isHonorarium = item.attest?.model === "Arvode";
                                    const types = getReimbursementTypesFromReimbursement(relatedModel);
                                    return (
                                        <tr
                                            key={index}
                                            className={`sl-row ${index < relatedData.items.length - 1 ? 'sl-row-divider' : ''}`}
                                        >
                                            <td className="sl-td-base att-related-nowrap">{relatedModel.visualId || ''}</td>
                                            <td className="sl-td-base att-related-nowrap">
                                                {relatedModel.date ? ChangeDateFormat('date', relatedModel.date) : ''}
                                            </td>
                                            <td className="sl-td-base">{relatedModel.purpose || ''}</td>
                                            <td className="sl-td-base">
                                                {isHonorarium
                                                    ? t('honorarium.honorarium')
                                                    : <ReimbursementTypeBadges types={types} />}
                                            </td>
                                            <td className="sl-td-base">{item.attest?.orgName || ''}</td>
                                            <td className={`sl-td-base att-related-amount att-related-nowrap ${item.attest?.amount >= 0 ? 'text-green' : 'text-red'}`}>
                                                {item.attest?.amount !== undefined ? `${ToCustomDecimal(item.attest.amount)} kr` : ''}
                                            </td>
                                            <td className="sl-td-base att-related-nowrap">
                                                {item.attest?.createdAt ? ChangeDateFormat('dateTime', item.attest.createdAt) : ''}
                                            </td>
                                            <td className="sl-td-base att-related-nowrap">
                                                {relatedModel.status ? statusPill(relatedModel.status) : ''}
                                            </td>
                                            <td className="sl-td-base sl-td-action">
                                                <button
                                                    type="button"
                                                    className="reimbursement-action-menu-trigger"
                                                    onClick={() => handleClick(item.attest?.id)}
                                                    aria-label={t('button.info')}
                                                >
                                                    <i className="bx bx-link-external reimbursement-action-menu-icon" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
                {paginationControls}
            </div>
        );
    }

    return (
        <Row className="mt-5">
            <Col lg={12}>
                {relatedData?.items.length === 1 && relatedData?.pagination.totalPages === 1 ? (
                    <h3 className="rokkitt-font">{t('attest.singleRelatedProceeding')}</h3>
                ) : (
                    <h3 className="rokkitt-font">{t('attest.relatedProceedings')}</h3>
                )}
                <Card className="p-4">
                    <Row>
                        <Col lg={12}>
                            {table}
                        </Col>
                    </Row>
                </Card>
                {paginationControls}
            </Col>
        </Row>
    );
}
